import React, { useMemo, useCallback } from "react";
import { TooltipProps } from "recharts";
import ChartContent from "./ThermalHeatMapChartContent";
import { formatTime, useProgressiveCount } from "../../hooks/ThermalHeatMapHooks";
import NewLoader from "../../../../../../../CommonComponents/NewLoader";

export interface SubsystemChartItem {
  nodeKey: string;
  subsystemName: string;
  faceLabel: string;
}

export interface ThermalSubsystemChartsGridProps {
  items: SubsystemChartItem[];
  chartData: Record<string, number>[];
  thresholdLines: { key: string; y: number; color: string }[];
  breachAreas: { x1: number; x2: number; color: string; stroke: string }[];
  eclipseAreas?: { x1: number; x2: number; color: string; stroke: string }[];
  phaseStripSegments?: { x1: number; x2: number; type: "Eclipse" | "Sunlit" }[];
  renderTooltip: (props: TooltipProps<number, string>) => React.ReactNode;
  onZoomSelect?: (domain: [number, number]) => void;
  isFullscreen?: boolean;
  subsystemThresholds?: Record<string, Array<{ key: string; y: number; color: string }>>;
  subsystemBreachAreas?: Record<string, Array<{ x1: number; x2: number; color: string; stroke: string }>>;
}

const ThermalSubsystemChartsGrid: React.FC<ThermalSubsystemChartsGridProps> = ({
  items,
  chartData,
  thresholdLines,
  breachAreas,
  eclipseAreas = [],
  phaseStripSegments = [],
  renderTooltip,
  onZoomSelect,
  isFullscreen = false,
  subsystemThresholds = {},
  subsystemBreachAreas = {},
}) => {
  const subsystemsPerRow = 3;
  const mountedCount = useProgressiveCount(items);

  // Pre-compute nodeKey -> subsystemName lookup for O(1) access in tooltip
  const nodeKeyToSubsystemMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of items) {
      map[item.nodeKey] = item.subsystemName;
    }
    return map;
  }, [items]);

  // Custom tooltip that shows subsystem name instead of node key
  const renderSubsystemTooltip = useCallback(
    (props: TooltipProps<number, string>) => {
      if (!props.active || !props.payload?.length) return null;
      const timeSec = (props.payload[0]?.payload as { time: number })?.time ?? 0;

      // Look up subsystem name from pre-computed map
      const nodeKey = String(props.payload[0]?.dataKey ?? '');
      const subsystemName = nodeKeyToSubsystemMap[nodeKey] ?? nodeKey;

      return (
        <div className="thm-chart-tooltip">
          <div className="thm-chart-tooltip-time">{formatTime(timeSec)}</div>
          {props.payload.map((item, idx) => (
            <div key={idx} className="thm-chart-tooltip-row">
              <span className="thm-chart-tooltip-dot" style={{ background: item.color as string }} />
              <span className="thm-chart-tooltip-face">{subsystemName}</span>
              <span className="thm-chart-tooltip-val">
                {(item.value as number)?.toFixed(2)} °C
              </span>
            </div>
          ))}
        </div>
      );
    },
    [nodeKeyToSubsystemMap]
  );

  // Compute grid template rows: dynamic number of rows based on items
  const gridTemplateRows = useMemo(() => {
    const totalSubsystems = items.length;
    const chartRows = Math.ceil(totalSubsystems / subsystemsPerRow);
    return Array(chartRows).fill("1fr").join(" ");
  }, [items.length]);

  // One stable array per cell, so ChartContent's memo is not defeated by a new array on every wave
  const cellVisibleFaceKeys = useMemo(() => items.map((item) => [item.nodeKey]), [items]);

  if (items.length === 0) {
    return (
      <div className="thm-chart-empty">
        Select subsystems to display
      </div>
    );
  }

  return (
    <div className="thm-chart-subsystem-grid" style={{ gridTemplateRows }}>
      {items.map((item, index) => (
        <div key={`${item.nodeKey}:${item.subsystemName}`} className="thm-chart-subsystem-cell">
          <div className="thm-chart-subsystem-cell-title">
            {item.subsystemName} ({item.faceLabel}): Temperature Vs Time
          </div>
          <div className="thm-chart-subsystem-cell-chart">
            {index < mountedCount ? (
              <ChartContent
                chartData={chartData}
                visibleFaceKeys={cellVisibleFaceKeys[index]}
                thresholdLines={subsystemThresholds[item.nodeKey] ?? thresholdLines}
                breachAreas={subsystemBreachAreas[item.nodeKey] ?? breachAreas}
                eclipseAreas={eclipseAreas}
                phaseStripSegments={phaseStripSegments}
                renderTooltip={renderSubsystemTooltip}
                onZoomSelect={onZoomSelect}
                isFullscreen={false}
              />
            ) : (
              <NewLoader canvasHeight="100%" canvasWidth="100%" loaderpage={undefined} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default React.memo(ThermalSubsystemChartsGrid);
